import { registerDecorator, type ValidationOptions } from 'class-validator';
import { normalizeMobileToE164 } from './is-mobile';

/** Fisso italiano: prefisso distrettuale con lo 0 iniziale, 6-11 cifre totali. */
const IT_LANDLINE_REGEX = /^0\d{5,10}$/;

/**
 * Normalizza un telefono aziendale (cellulare o fisso) a E.164, o null.
 * Stesse regole di `normalizeMobileToE164`, più il fisso italiano digitato
 * senza prefisso internazionale: lo 0 del distretto resta (`+39080…`), come
 * da piano di numerazione italiano.
 */
export function normalizePhoneToE164(value: string): string | null {
  const mobile = normalizeMobileToE164(value);
  if (mobile) {
    return mobile;
  }
  const digits = value.trim().replace(/[.\s\-()/]/g, '');
  if (IT_LANDLINE_REGEX.test(digits)) {
    return `+39${digits}`;
  }
  return null;
}

/** Italian landline (district code with leading 0), in any accepted notation. */
export function isItalianLandline(value: string): boolean {
  return normalizePhoneToE164(value)?.startsWith('+390') ?? false;
}

export function isValidPhone(value: unknown): boolean {
  return typeof value === 'string' && normalizePhoneToE164(value) !== null;
}

/** Telefono aziendale: cellulare o fisso. Empty/null values are left to @IsOptional. */
export function IsPhone(validationOptions?: ValidationOptions) {
  return (object: object, propertyName: string) => {
    registerDecorator({
      name: 'isPhone',
      target: object.constructor,
      propertyName,
      options: validationOptions,
      validator: {
        validate(value: unknown) {
          return isValidPhone(value);
        },
        defaultMessage() {
          return 'Inserisci un numero di telefono valido (es. 080 123 4567 o 333 123 4567)';
        },
      },
    });
  };
}
